/* eslint max-lines-per-function: ["error", 168]*/
import React, { useState } from 'react';
import {
  OverlayTrigger,
  Tooltip,
  Modal,
  Button,
  Form,
  Alert,
} from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import {
  CreateEmailResendDto,
  GetEmailDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../translations';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { FormRow } from '../../../app/components/FormRow';
import { validateResend } from '../../../common/pokCore/contexts/EmailContext';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';

import {
  canResendEmail,
  emailResendFromDateText,
} from './emailResendAvailability';

export const EmailResendLinkWithOverride = (props: {
  emailId: string;
  email: GetEmailDto;
  setRefresh?: () => void;
  setLoading?: (loading: boolean) => void;
}) => {
  const initialResendDto = (): CreateEmailResendDto => ({
    id: props.emailId,
    emailTo: props.email?.sentTo || '',
    emailCc: props.email?.sentCc || '',
    emailBcc: props.email?.sentBcc || '',
  });

  const [showModal, setShowModal] = useState(false);
  const [resendDto, setResendDto] =
    useState<CreateEmailResendDto>(initialResendDto);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('applicationLng');

  const resendAllowed = canResendEmail(props.email?.sendDate);

  const propertyChange = (obj: Partial<CreateEmailResendDto>) => {
    setResendDto(prev => ({ ...prev, ...obj }));
  };

  const handleModalOpen = () => {
    setResendDto(initialResendDto());
    setSaveErrors([]);
    setShowSaveErrors(false);
    setShowModal(true);
  };

  const handleResendWithOverride = async () => {
    if (props.setLoading) {
      props.setLoading(true);
    }
    const status = await validateResend(resendDto);
    if (!status.valid) {
      setSaveErrors(status.errors);
      setShowSaveErrors(true);
      if (props.setLoading) {
        props.setLoading(false);
      }
      return;
    }
    pok.emails
      .resend(resendDto)
      .then(() => {
        if (props.setRefresh) {
          props.setRefresh();
        }
        if (props.setLoading) {
          props.setLoading(false);
        }
        setShowModal(false);
      })
      .catch(error => {
        notifications.caughtError(error as ResponseError);
        if (props.setLoading) {
          props.setLoading(false);
        }
      });
  };

  return (
    <>
      <Button variant="link" className="ms-1" onClick={handleModalOpen}>
        <OverlayTrigger
          overlay={
            <Tooltip style={{ position: 'fixed' }}>
              {t(tk.email.resendToAnotherAddress)}
            </Tooltip>
          }
        >
          <Icon.EnvelopePlus size={18} />
        </OverlayTrigger>
      </Button>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        size="xl"
      >
        <Modal.Header>
          <Modal.Title>{t(tk.email.resendEmail)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!resendAllowed && (
            <Alert variant="warning" className="mb-0">
              {t(tk.email.resendNotAvailableBeforeDate, {
                date: emailResendFromDateText(),
              })}
            </Alert>
          )}
          {resendAllowed && (
            <>
              <Alert variant="info">
                {t(tk.email.emailWillBeResentToSpecifiedAddresses)}
              </Alert>
              <Form className="d-grid gap-4 mb-3">
                <FormRow
                  controlId="emailTo"
                  label={t(tk.email.recipientEmailAddresses)}
                  tooltipKey={TooltipKey.emailToTooltip}
                >
                  <Form.Control
                    type="text"
                    value={resendDto.emailTo || ''}
                    onChange={e => propertyChange({ emailTo: e.target.value })}
                  />
                </FormRow>
                <FormRow
                  controlId="emailCC"
                  label={t(tk.email.ccEmailAddresses)}
                >
                  <Form.Control
                    type="text"
                    value={resendDto.emailCc || ''}
                    onChange={e => propertyChange({ emailCc: e.target.value })}
                  />
                </FormRow>
                <FormRow
                  controlId="emailBCC"
                  label={t(tk.email.bccEmailAddresses)}
                >
                  <Form.Control
                    type="text"
                    value={resendDto.emailBcc || ''}
                    onChange={e => propertyChange({ emailBcc: e.target.value })}
                  />
                </FormRow>
              </Form>
            </>
          )}
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="my-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowModal(false)}
          >
            {t(tk.button.cancel)}
          </Button>
          {resendAllowed && (
            <Button
              variant="outline-primary"
              onClick={handleResendWithOverride}
            >
              {t(tk.button.send)}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
